import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import apiClient from '@/lib/api';
import useAuthStore from '@/store/authStore';
import { Mail, Lock, Chrome, AlertCircle, MessageSquare, Github, Apple, Gamepad2 } from 'lucide-react';

const PROVIDER_ICONS = {
  google: Chrome,
  discord: MessageSquare,
  github: Github,
  microsoft: Chrome,
  apple: Apple,
  steam: Gamepad2,
  minecraft: Gamepad2,
};

const PROVIDER_COLORS = {
  google: 'hover:border-pista/60',
  discord: 'hover:border-[#5865F2]/60',
  github: 'hover:border-white/60',
  microsoft: 'hover:border-[#0078D4]/60',
  apple: 'hover:border-white/60',
  steam: 'hover:border-[#66C0F4]/60',
  minecraft: 'hover:border-[#8B7355]/60',
};

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(location.state?.error || '');
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [providers, setProviders] = useState([]);
  const [mockDialog, setMockDialog] = useState({ open: false, provider: null });
  const [mockUsername, setMockUsername] = useState('testuser');

  useEffect(() => {
    // Load enabled providers
    apiClient.get('/providers').then(res => {
      setProviders(res.data);
    }).catch(() => {});
  }, []);

  const handleProviderLogin = (provider) => {
    if (provider.type === 'google' && !provider.mocked) {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + '/auth/callback';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    // For mocked providers, show dialog to enter username
    setMockDialog({ open: true, provider });
    setMockUsername('testuser');
  };

  const handleMockLogin = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await apiClient.post(`/auth/mock/${mockDialog.provider.type}`, {
        username: mockUsername
      });
      setAuth(response.data.user, response.data.access_token);
      setMockDialog({ open: false, provider: null });
      navigate(location.state?.from?.pathname || '/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await apiClient.post('/auth/login', { email, password });
      
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setTempToken(response.data.temp_token);
      } else {
        setAuth(response.data.user, response.data.access_token);
        navigate(location.state?.from?.pathname || '/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await apiClient.post(`/2fa/verify?temp_token=${tempToken}`, {
        code: twoFACode
      });
      
      setAuth(response.data.user, response.data.access_token);
      navigate(location.state?.from?.pathname || '/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || '2FA verification failed.');
    } finally {
      setIsLoading(false);
    }
  };

  if (requires2FA) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-pista-glow pointer-events-none" />
        <Card className="w-full max-w-md relative z-10 border-border/60 backdrop-blur-sm bg-card/95" data-testid="2fa-verification-card">
          <CardHeader>
            <CardTitle className="text-2xl font-bold">Two-Factor Authentication</CardTitle>
            <CardDescription>Enter your 6-digit verification code</CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4" data-testid="error-alert">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <form onSubmit={handle2FAVerify} className="space-y-4">
              <div>
                <Label htmlFor="code">Verification Code</Label>
                <Input
                  id="code"
                  data-testid="2fa-code-input"
                  type="text"
                  placeholder="000000"
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value)}
                  maxLength={6}
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
                data-testid="verify-2fa-button"
              >
                {isLoading ? 'Verifying...' : 'Verify'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 relative overflow-hidden">
      {/* Ambient pista glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-pista-glow pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,hsl(82_42%_60%/0.08),transparent_50%)] pointer-events-none" />
      
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold mb-2 tracking-tight">
            <span className="text-pista">Lost</span>DrkC
          </h1>
          <p className="text-muted-foreground text-sm">One Account. Unlimited Services.</p>
        </div>

        <Card data-testid="login-card" className="border-border/60 backdrop-blur-sm bg-card/95">
          <CardHeader>
            <CardTitle className="text-2xl font-bold">Welcome back</CardTitle>
            <CardDescription>Sign in to your LostDrkC Account</CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4" data-testid="error-alert">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-4">
              <div className="space-y-2">
                {providers.map((provider) => {
                  const Icon = PROVIDER_ICONS[provider.type] || Chrome;
                  return (
                    <Button
                      key={provider.provider_id}
                      variant="outline"
                      className={`w-full transition-colors duration-200 ${PROVIDER_COLORS[provider.type] || ''}`}
                      onClick={() => handleProviderLogin(provider)}
                      data-testid={`${provider.type}-login-button`}
                    >
                      <Icon className="mr-2 h-4 w-4" />
                      Continue with {provider.name}
                      {provider.mocked && (
                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-pista/20 text-pista">MOCK</span>
                      )}
                    </Button>
                  );
                })}
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Or</span>
                </div>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground z-10" />
                    <Input
                      id="email"
                      data-testid="email-input"
                      type="email"
                      placeholder="you@example.com"
                      className="pl-10"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link
                      to="/forgot-password"
                      className="text-xs text-pista hover:opacity-80 transition-opacity"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground z-10" />
                    <Input
                      id="password"
                      data-testid="password-input"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                  data-testid="login-submit-button"
                >
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </Button>
              </form>
            </div>

            <p className="text-center text-sm text-muted-foreground mt-6">
              Don't have an account?{' '}
              <Link
                to="/signup"
                className="text-pista hover:opacity-80 font-medium transition-opacity"
                data-testid="signup-link"
              >
                Sign up
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Mock provider login dialog */}
      <Dialog open={mockDialog.open} onOpenChange={(open) => setMockDialog({ ...mockDialog, open })}>
        <DialogContent data-testid="mock-login-dialog">
          <DialogHeader>
            <DialogTitle>Mock {mockDialog.provider?.name} Login</DialogTitle>
            <DialogDescription>
              This provider is in mock mode. Enter a username to simulate the OAuth flow.
              Real credentials can be configured via the admin panel.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="mock-username">Username</Label>
              <Input
                id="mock-username"
                data-testid="mock-username-input"
                placeholder="testuser"
                value={mockUsername}
                onChange={(e) => setMockUsername(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMockDialog({ open: false, provider: null })}>
              Cancel
            </Button>
            <Button onClick={handleMockLogin} disabled={isLoading || !mockUsername} data-testid="mock-login-submit">
              {isLoading ? 'Signing in...' : `Continue as ${mockUsername || '...'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginPage;
